import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { api, date, humanise, timeOnly, localDayKey, localTimeValue } from '@/lib/api';
import type { NamedRef } from '@/lib/types';
import { useAuth, type Role } from '@/lib/auth';
import { Modal, useToast } from '@/components/ui/overlays';
import {
  Button, Field, Input, Select, Textarea, EmptyState, SkeletonRows,
} from '@/components/ui/primitives';

export interface TaskRecord {
  id: string;
  title: string;
  description: string | null;
  entityType: string | null;
  entityId: string | null;
  entityLabel: string | null;
  customerName: string | null;
  dueDate: string;
  dueTime: string | null;
  priority: 'URGENT' | 'HIGH' | 'NORMAL' | 'LOW';
  status: string;
  snoozeCount: number;
  autoGenerated: boolean;
  triggerType: string | null;
  href: string | null;
  user?: { id: string; name: string };
}

interface Agenda {
  overdue: TaskRecord[];
  today: TaskRecord[];
  upcoming: Array<{ date: string; tasks: TaskRecord[] }>;
  counts: { overdue: number; today: number; thisWeek: number };
  canSeeAll: boolean;
}

/**
 * Priority dot.
 *
 * Urgent gets the orange dot — the direction's critical-flag colour — and
 * nothing else on the dashboard competes for it. High/normal/low resolve down
 * the neutral scale so the eye lands on genuinely urgent work first.
 */
const PRIORITY_DOT: Record<TaskRecord['priority'], string> = {
  URGENT: 'bg-primary',
  HIGH: 'bg-negative',
  NORMAL: 'bg-ink-tertiary',
  LOW: 'bg-rule',
};

const TRIGGER_LABEL: Record<string, string> = {
  quote_follow_up: 'Quote follow-up',
  quote_expiry: 'Quote expiring',
  trial_day_21: 'Trial day 21',
  trial_day_28: 'Trial day 28',
  trial_overdue: 'Trial overdue',
  delivery_follow_up: 'Delivery follow-up',
  warranty_expiry: 'Warranty expiring',
  opportunity_stale: 'Stale opportunity',
  payment_chase: 'Payment chase',
  po_landing: 'PO landing',
};

export function TasksWidget() {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [scope, setScope] = useState<'mine' | 'all'>('mine');
  const [priority, setPriority] = useState('');
  const [triggerType, setTriggerType] = useState('');
  const [snoozing, setSnoozing] = useState<TaskRecord | null>(null);
  const [editing, setEditing] = useState<TaskRecord | null>(null);
  const [creating, setCreating] = useState(false);

  const query = { scope, ...(priority ? { priority } : {}), ...(triggerType ? { triggerType } : {}) };

  const { data, isLoading } = useQuery({
    queryKey: ['task-agenda', query],
    queryFn: () => api.get<Agenda>('/api/tasks/agenda', query),
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['task-agenda'] });
    void qc.invalidateQueries({ queryKey: ['tasks'] });
  };

  const complete = useMutation({
    mutationFn: (id: string) => api.post(`/api/tasks/${id}/complete`),
    onSuccess: () => { toast('Task completed.', 'success'); refresh(); },
    onError: (e) => toast(e.message, 'error'),
  });

  if (isLoading) return <SkeletonRows rows={6} cols={3} />;

  const isEmpty =
    (data?.overdue.length ?? 0) === 0 &&
    (data?.today.length ?? 0) === 0 &&
    (data?.upcoming.length ?? 0) === 0;

  const row = (task: TaskRecord) => (
    <div key={task.id} className="rule-hairline flex items-start gap-3 py-2.5">
      <span
        aria-hidden
        className={clsx('mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full', PRIORITY_DOT[task.priority])}
        title={humanise(task.priority)}
      />

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2">
          {task.href ? (
            <Link to={task.href} className="text-body text-ink hover:underline">{task.title}</Link>
          ) : (
            <span className="text-body text-ink">{task.title}</span>
          )}
          {task.autoGenerated && task.triggerType && (
            <span className="label text-ink-tertiary">
              {TRIGGER_LABEL[task.triggerType] ?? humanise(task.triggerType)}
            </span>
          )}
        </div>

        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 font-sans text-caption text-ink-tertiary">
          {task.customerName && <span className="text-ink-secondary">{task.customerName}</span>}
          <span className="tabular">
            {date(task.dueDate)}{task.dueTime ? ` · ${timeOnly(task.dueTime)}` : ''}
          </span>
          {task.snoozeCount > 0 && <span>snoozed ×{task.snoozeCount}</span>}
          {scope === 'all' && task.user && <span>{task.user.name}</span>}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2.5">
        <button onClick={() => complete.mutate(task.id)} className="label text-positive hover:opacity-70" title="Complete">✓</button>
        <button onClick={() => setSnoozing(task)} className="label text-ink-secondary hover:text-ink" title="Snooze">Snooze</button>
        <button onClick={() => setEditing(task)} className="label text-ink-tertiary hover:text-ink" title="Edit">Edit</button>
        {task.href && (
          <button onClick={() => navigate(task.href!)} className="label text-ink-tertiary hover:text-ink" title="Go to record">→</button>
        )}
      </div>
    </div>
  );

  return (
    <div>
      {/* ─── Controls ─────────────────────────────────────────────────────── */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 font-sans text-caption">
          <span className={clsx('tabular', (data?.counts.overdue ?? 0) > 0 ? 'text-negative' : 'text-ink-tertiary')}>
            {data?.counts.overdue ?? 0} overdue
          </span>
          <span className="text-ink-tertiary">·</span>
          <span className="tabular text-ink">{data?.counts.today ?? 0} today</span>
          <span className="text-ink-tertiary">·</span>
          <span className="tabular text-ink-tertiary">{data?.counts.thisWeek ?? 0} this week</span>
        </div>

        {/* Widths are generous enough for the longest option in each list —
            "Stale opportunity" is the one that decides the last of them. */}
        <div className="flex flex-wrap items-center gap-2">
          {data?.canSeeAll && (
            <Select
              size="sm"
              value={scope}
              aria-label="Whose tasks"
              onChange={(e) => setScope(e.target.value as 'mine' | 'all')}
              options={[{ value: 'mine', label: 'My tasks' }, { value: 'all', label: 'All users' }]}
              className="w-28"
            />
          )}
          <Select
            size="sm"
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            placeholder="Any priority"
            options={['URGENT', 'HIGH', 'NORMAL', 'LOW'].map((p) => ({ value: p, label: humanise(p) }))}
            className="w-32"
          />
          <Select
            size="sm"
            value={triggerType}
            onChange={(e) => setTriggerType(e.target.value)}
            placeholder="Any type"
            options={Object.entries(TRIGGER_LABEL).map(([value, label]) => ({ value, label }))}
            className="w-44"
          />
          <Button size="sm" variant="primary" onClick={() => setCreating(true)}>Add task</Button>
        </div>
      </div>

      {isEmpty ? (
        <EmptyState message="Nothing outstanding. Follow-ups appear here automatically as quotes go out and trials progress." />
      ) : (
        <>
          {data!.overdue.length > 0 && (
            <section className="mb-5">
              <h3 className="label mb-1 border-b-2 border-negative pb-1 text-negative">
                Overdue · {data!.overdue.length}
              </h3>
              {data!.overdue.map(row)}
            </section>
          )}

          {data!.today.length > 0 && (
            <section className="mb-5">
              <h3 className="label mb-1 border-b-2 border-secondary pb-1 text-ink">
                Today · {data!.today.length}
              </h3>
              {data!.today.map(row)}
            </section>
          )}

          {data!.upcoming.length > 0 && (
            <section>
              <h3 className="label mb-1 border-b-2 border-rule pb-1 text-ink-tertiary">Upcoming</h3>
              {data!.upcoming.map((day) => (
                <div key={day.date} className="mt-3">
                  <div className="label mb-0.5 text-ink-tertiary">
                    {/* Split rather than `new Date(str)` — a bare YYYY-MM-DD is
                        parsed as UTC and would render as the previous day. */}
                    {(() => {
                      const [y, m, d] = day.date.split('-').map(Number);
                      return new Date(y, m - 1, d).toLocaleDateString('en-GB', {
                        weekday: 'long', day: '2-digit', month: 'short',
                      });
                    })()}
                  </div>
                  {day.tasks.map(row)}
                </div>
              ))}
            </section>
          )}
        </>
      )}

      {snoozing && <SnoozeDialog task={snoozing} onClose={() => setSnoozing(null)} onDone={refresh} />}
      {(creating || editing) && (
        <TaskDialog
          task={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onDone={refresh}
        />
      )}
    </div>
  );
}

// ─── Snooze ──────────────────────────────────────────────────────────────────

export function SnoozeDialog({
  task, onClose, onDone, onSubmitOverride,
}: {
  task: TaskRecord;
  onClose: () => void;
  onDone: () => void;
  /**
   * Used by the bulk-snooze flow, which posts to the bulk endpoint so each
   * selected task still gets its own timeline note against the right customer.
   */
  onSubmitOverride?: (until: string, reason: string, note?: string) => Promise<unknown>;
}) {
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [preset, setPreset] = useState('tomorrow');
  const [customAt, setCustomAt] = useState('');

  const { data: options } = useQuery({
    queryKey: ['snooze-options'],
    queryFn: () => api.get<{
      reasons: Array<{ value: string; label: string }>;
      presets: Array<{ key: string; label: string; at: string }>;
    }>('/api/tasks/snooze-options'),
  });

  const snooze = useMutation({
    mutationFn: () => {
      const until = (preset === 'custom'
        ? new Date(customAt).toISOString()
        : options?.presets.find((p) => p.key === preset)?.at) ?? new Date().toISOString();
      if (onSubmitOverride) return onSubmitOverride(until, reason, note || undefined);
      return api.post(`/api/tasks/${task.id}/snooze`, { until, reason, note: note || undefined });
    },
    onSuccess: () => {
      toast('Task snoozed. The reason has been logged against the record.', 'success');
      onDone();
      onClose();
    },
    onError: (e) => toast(e.message, 'error'),
  });

  const chosen = options?.presets.find((p) => p.key === preset);
  const canSubmit = reason && (preset !== 'custom' || customAt) && (reason !== 'other' || note.trim());

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Snooze task"
      size="sm"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!canSubmit} isLoading={snooze.isPending} onClick={() => snooze.mutate()}>
            Snooze
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-body text-ink-secondary">{task.title}</p>

        <Field label="Reason" required hint="Logged on the record's activity timeline.">
          <Select
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why is this being put off?"
            options={options?.reasons ?? []}
          />
        </Field>

        {reason === 'other' && (
          <Field label="Detail" required>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
          </Field>
        )}

        <Field label="Reschedule to" required>
          <Select
            value={preset}
            onChange={(e) => setPreset(e.target.value)}
            options={[
              ...(options?.presets ?? []).map((p) => ({ value: p.key, label: p.label })),
              { value: 'custom', label: 'Custom date and time' },
            ]}
          />
        </Field>

        {preset === 'custom' ? (
          <Field label="Date and time" required>
            <Input type="datetime-local" value={customAt} onChange={(e) => setCustomAt(e.target.value)} />
          </Field>
        ) : chosen ? (
          <p className="font-sans text-caption text-ink-tertiary">
            Returns {new Date(chosen.at).toLocaleString('en-GB', {
              weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
            })}
            {' — weekends and bank holidays are skipped.'}
          </p>
        ) : null}

        {task.snoozeCount > 0 && (
          <p className="rounded-md border-l-[3px] border-warning bg-warning/[0.06] py-2 pl-3 pr-3 text-caption text-ink-secondary">
            Already snoozed {task.snoozeCount} time{task.snoozeCount === 1 ? '' : 's'}.
          </p>
        )}
      </div>
    </Modal>
  );
}

// ─── Create / edit ───────────────────────────────────────────────────────────

export function TaskDialog({
  task, entityType, entityId, entityLabel, customerName, onClose, onDone,
}: {
  task?: TaskRecord | null;
  entityType?: string;
  entityId?: string;
  entityLabel?: string;
  customerName?: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const { user, can } = useAuth();
  const isEdit = !!task;

  const [form, setForm] = useState({
    title: task?.title ?? '',
    description: task?.description ?? '',
    dueDate: task?.dueDate ? task.dueDate.slice(0, 10) : localDayKey(new Date()),
    // Read back in the same zone it was typed in. Formatting the stored instant
    // as UTC showed 13:00 for a task entered at 14:00 BST, and saving from that
    // box wrote 12:00 — so every edit walked the time an hour earlier.
    dueTime: task?.dueTime ? localTimeValue(task.dueTime) : '',
    priority: task?.priority ?? 'NORMAL',
    userId: task?.user?.id ?? user?.id ?? '',
  });

  const { data: users } = useQuery({
    queryKey: ['users-lookup'],
    queryFn: () => api.get<Array<NamedRef & { role: Role }>>('/api/settings/users'),
    enabled: can('users.manage'),
  });

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        title: form.title,
        description: form.description || undefined,
        dueDate: form.dueDate,
        // Combined into a full timestamp so "due at 14:00" survives a timezone.
        dueTime: form.dueTime ? new Date(`${form.dueDate}T${form.dueTime}`).toISOString() : null,
        priority: form.priority,
        userId: form.userId || undefined,
        ...(isEdit ? {} : { entityType, entityId, entityLabel, customerName }),
      };
      return isEdit ? api.patch(`/api/tasks/${task!.id}`, payload) : api.post('/api/tasks', payload);
    },
    onSuccess: () => {
      toast(isEdit ? 'Task updated.' : 'Task created.', 'success');
      onDone();
      onClose();
    },
    onError: (e) => toast(e.message, 'error'),
  });

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isEdit ? 'Edit task' : 'Add task'}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!form.title.trim()} isLoading={save.isPending} onClick={() => save.mutate()}>
            {isEdit ? 'Save' : 'Create task'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Title" required>
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} autoFocus />
        </Field>

        <Field label="Description" hint="Markdown is supported.">
          <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </Field>

        {!isEdit && entityLabel && (
          <p className="border-l-2 border-rule py-1 pl-3 text-caption text-ink-tertiary">
            Linked to {entityType} {entityLabel}
            {customerName ? ` · ${customerName}` : ''}
          </p>
        )}

        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Due date" required>
            <Input type="date" value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} />
          </Field>
          <Field label="Due time" hint="Optional">
            <Input type="time" value={form.dueTime} onChange={(e) => setForm({ ...form, dueTime: e.target.value })} />
          </Field>
          <Field label="Priority">
            <Select
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value as TaskRecord['priority'] })}
              options={['URGENT', 'HIGH', 'NORMAL', 'LOW'].map((p) => ({ value: p, label: humanise(p) }))}
            />
          </Field>
        </div>

        {can('users.manage') && (
          <Field label="Assign to">
            <Select
              value={form.userId}
              onChange={(e) => setForm({ ...form, userId: e.target.value })}
              options={(users ?? []).map((u) => ({ value: u.id, label: `${u.name} (${humanise(u.role)})` }))}
            />
          </Field>
        )}
      </div>
    </Modal>
  );
}

/** "Add task" button for entity detail pages — pre-links the record. */
export function AddTaskButton({
  entityType, entityId, entityLabel, customerName, size = 'sm',
}: {
  entityType: string;
  entityId: string;
  entityLabel?: string;
  customerName?: string;
  size?: 'sm' | 'md';
}) {
  const [isOpen, setIsOpen] = useState(false);
  const qc = useQueryClient();

  return (
    <>
      <Button size={size} onClick={() => setIsOpen(true)}>Add task</Button>
      {isOpen && (
        <TaskDialog
          entityType={entityType}
          entityId={entityId}
          entityLabel={entityLabel}
          customerName={customerName}
          onClose={() => setIsOpen(false)}
          onDone={() => {
            void qc.invalidateQueries({ queryKey: ['task-agenda'] });
            void qc.invalidateQueries({ queryKey: ['tasks-for', entityType, entityId] });
          }}
        />
      )}
    </>
  );
}

export { TRIGGER_LABEL, PRIORITY_DOT };
